import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Badge,
  Flex,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import {
  TablerSearch,
  TablerX,
  TablerChevronDown,
  TablerChevronUp,
  TablerStar,
  TablerCheck,
  TablerStack2,
} from '../../icons';
import {
  RECAP_BUTTON_HEIGHT,
  RECAP_OPALITE_BLUE,
  RECAP_OPALITE_BLUE_BORDER,
  RECAP_OPALITE_BLUE_TINT_08,
} from './recapButtonStyles';
import {plannerSearchHighlightNode} from '../planner/PlannerSearchHighlight';
import usePlannerStyles, {useScrollbarSx} from '../planner/usePlannerStyles';

const ACCENT = '#636363';
const SIDEBAR_HEIGHT_MOTION_MS = 200;
const SIDEBAR_HEIGHT_MOTION_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const CATEGORY_STICKY_HEIGHT = 40;
const DRAWER_MOTION_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

// Fade + gentle slide for the rows revealed when a category drawer opens.
// Pairs with the scroll container's height transition so the card grows
// smoothly while its contents settle in, instead of popping. Honors
// reduced-motion per WCAG 2.3.3.
const DRAWER_CONTENT_SX = {
  animation: `templateDrawerExpand 260ms ${DRAWER_MOTION_EASE} both`,
  '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
  '@keyframes templateDrawerExpand': {
    '0%': {opacity: 0, transform: 'translateY(-6px)'},
    '100%': {opacity: 1, transform: 'translateY(0)'},
  },
} as const;

const CHEVRON_MOTION_SX = {
  transition: `transform 260ms ${DRAWER_MOTION_EASE}, color 150ms ease`,
  '@media (prefers-reduced-motion: reduce)': {transition: 'color 150ms ease'},
} as const;

function fuzzyMatch(needle: any, haystack: any) {
  if (!needle || !haystack) return false;
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

/**
 * Searchable template picker matching iOS NoteGenerationFlowView.
 * WCAG 2.1 AA compliant: min 44px touch targets, ≥14px body text, ≥12px badges.
 */
const SearchableTemplatePicker = ({
  templates = [],
  selectedId,
  onSelect,
  suggestedId,
  isLoadingSuggestion = false,
  isDisabled = false,
  accentColor = ACCENT,
  cardBg = 'white',
  borderColor = 'gray.200',
  subtleText = 'gray.500',
  maxH = '520px',
  listVariant = 'card',
}: Record<string, any>) => {
  const plannerStyles = usePlannerStyles();
  // Always-visible, easy-to-grab scrollbar (the planner token): the template
  // list is one of the flow's main scroll regions, and a hidden overlay
  // scrollbar left users with no handle to grab on twitchy mice.
  const scrollbarSx = useScrollbarSx();
  const [searchText, setSearchText] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState(() =>
    listVariant === 'clinicalNote'
      ? new Set(templates.map((t: any) => t.specialty_category).filter(Boolean))
      : new Set(),
  );
  const selectedStickyRef = useRef<any>(null);
  const categoryRefs = useRef<Record<string, any>>({});
  const scrollContainerRef = useRef<any>(null);
  const contentRef = useRef<any>(null);
  const [listHeight, setListHeight] = useState<number | null>(null);
  const [selectedStickyHeight, setSelectedStickyHeight] = useState(0);
  const isPatientBrowserList = listVariant === 'patientBrowser';
  const isClinicalNoteList = listVariant === 'clinicalNote';
  const usesBarRows = isPatientBrowserList || isClinicalNoteList;
  const getListMaxHeightPx = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element || typeof window === 'undefined') return null;
    const parsed = Number.parseFloat(
      window.getComputedStyle(element).maxHeight,
    );
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const rememberListHeight = useCallback(() => {
    const element = scrollContainerRef.current;
    const content = contentRef.current;
    if (!element || !content) return;

    const maxHeight = getListMaxHeightPx();
    // Measure the natural height of the inner content wrapper rather than the
    // scroll container's `scrollHeight`. Once the container has a fixed height,
    // its `scrollHeight` can never report *less* than that height, so the list
    // would never shrink back when categories collapse — leaving dead space
    // above the primary action. The content wrapper is unconstrained, so its
    // box height always reflects the true content height.
    const measuredHeight = Math.ceil(content.getBoundingClientRect().height);
    if (measuredHeight <= 0) return;

    // Track the actual content height (capped by the responsive max) so the
    // card hugs its contents. Collapsing categories shrinks the list back
    // down instead of leaving a tall band of empty space above the primary
    // action — and the CSS height transition keeps the resize smooth. When a
    // big category is expanded past the cap, the list pins to the cap and
    // scrolls internally.
    const nextMeasuredHeight =
      maxHeight == null ? measuredHeight : Math.min(measuredHeight, maxHeight);
    setListHeight(previousHeight =>
      previousHeight === nextMeasuredHeight
        ? previousHeight
        : nextMeasuredHeight,
    );
  }, [getListMaxHeightPx]);

  const clampListHeightToViewport = useCallback(() => {
    const maxHeight = getListMaxHeightPx();
    if (maxHeight == null) return;
    setListHeight(previousHeight => {
      if (previousHeight == null) return previousHeight;
      const nextHeight = Math.min(previousHeight, maxHeight);
      return previousHeight === nextHeight ? previousHeight : nextHeight;
    });
  }, [getListMaxHeightPx]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let frame: any = null;
    const schedule =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback: any) => window.setTimeout(callback, 0);
    const cancel =
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame.bind(window)
        : window.clearTimeout.bind(window);
    const handleResize = () => {
      if (frame != null) cancel(frame);
      frame = schedule(() => {
        frame = null;
        // Clamp to the new responsive cap first so the list never exceeds the
        // viewport after a resize, then re-measure to hug the content.
        clampListHeightToViewport();
        rememberListHeight();
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (frame != null) cancel(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, [clampListHeightToViewport, rememberListHeight]);

  const filtered = useMemo(() => {
    if (!searchText.trim()) return templates;
    return templates.filter(
      (t: any) =>
        fuzzyMatch(searchText, t.name) ||
        fuzzyMatch(searchText, t.description || '') ||
        fuzzyMatch(searchText, t.specialty_category || ''),
    );
  }, [templates, searchText]);

  const grouped = useMemo(() => {
    const hasCats = filtered.some((t: any) => t.specialty_category);
    if (!hasCats) return [{category: null, templates: filtered}];
    const uncategorized = filtered.filter((t: any) => !t.specialty_category);
    const catOrder: any[] = [];
    const catMap: Record<string, any[]> = {};
    filtered.forEach((t: any) => {
      if (!t.specialty_category) return;
      if (!catMap[t.specialty_category]) {
        catOrder.push(t.specialty_category);
        catMap[t.specialty_category] = [];
      }
      catMap[t.specialty_category].push(t);
    });
    for (const cat of catOrder) {
      catMap[cat].sort(
        (a: any, b: any) => (a.display_order ?? 999) - (b.display_order ?? 999),
      );
    }
    const groups = [];
    if (uncategorized.length > 0)
      groups.push({category: null, templates: uncategorized});
    catOrder.forEach(cat =>
      groups.push({category: cat, templates: catMap[cat]}),
    );
    return groups;
  }, [filtered]);

  const suggestedTemplate = suggestedId
    ? templates.find((t: any) => t.id === suggestedId)
    : null;
  const selectedTemplate = selectedId
    ? templates.find((t: any) => t.id === selectedId)
    : null;

  const measureSelectedStickyHeight = useCallback(() => {
    const element = selectedStickyRef.current;
    const nextHeight = element
      ? Math.ceil(element.getBoundingClientRect().height)
      : 0;
    setSelectedStickyHeight(previousHeight =>
      previousHeight === nextHeight ? previousHeight : nextHeight,
    );
  }, []);

  useLayoutEffect(() => {
    measureSelectedStickyHeight();
  }, [
    isDisabled,
    listVariant,
    measureSelectedStickyHeight,
    selectedTemplate?.description,
    selectedTemplate?.id,
    selectedTemplate?.name,
    suggestedId,
  ]);

  useEffect(() => {
    const element = selectedStickyRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureSelectedStickyHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measureSelectedStickyHeight, selectedTemplate?.id]);

  useLayoutEffect(() => {
    rememberListHeight();
  }, [
    collapsedCategories,
    filtered.length,
    grouped,
    isLoadingSuggestion,
    listVariant,
    maxH,
    rememberListHeight,
    searchText,
    selectedTemplate?.id,
    suggestedTemplate?.id,
  ]);

  useEffect(() => {
    if (collapsedCategories.size > 0 || searchText) return;
    const cats = new Set(
      templates.map((t: any) => t.specialty_category).filter(Boolean),
    );
    if (cats.size === 0) return;
    if (!isClinicalNoteList) {
      // Other lists open expanded until the list is long, then collapse all
      // but the selected template's category.
      if (templates.length <= 8) return;
      const selectedTemplate = templates.find((t: any) => t.id === selectedId);
      const selectedCat = selectedTemplate?.specialty_category;
      if (selectedCat) cats.delete(selectedCat);
    }
    // Clinical note modal opens fully collapsed: every category is a closed
    // accordion the user expands to browse.
    setCollapsedCategories(cats);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  const toggleCategory = (cat: any) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      const isExpanding = next.has(cat);
      if (isExpanding) next.delete(cat);
      else next.add(cat);

      if (isExpanding) {
        // Make sure the just-expanded category header anchors near the top of
        // the list's own visible area without nudging the whole page.
        const scrollInner = () => {
          const container = scrollContainerRef.current;
          const el = categoryRefs.current[cat];
          if (!container || !el) return;
          const containerTop = container.getBoundingClientRect().top;
          const elTop = el.getBoundingClientRect().top;
          const target = container.scrollTop + (elTop - containerTop);
          container.scrollTo({top: Math.max(0, target), behavior: 'smooth'});
        };
        requestAnimationFrame(scrollInner);
        setTimeout(scrollInner, 280);
      }

      return next;
    });
  };

  const categoryHeaderBg = usesBarRows ? plannerStyles.rowBg : cardBg;
  const categoryStickyTop =
    selectedTemplate && !isClinicalNoteList ? `${selectedStickyHeight}px` : 0;

  const renderTemplateOption = (
    t: any,
    {
      isPinned = false,
      templateRef = undefined,
    }: {isPinned?: boolean; templateRef?: any} = {},
  ) => {
    const isSelected = t.id === selectedId;
    const isSuggested = t.id === suggestedId;
    const nameNode = plannerSearchHighlightNode(t.name, searchText);
    const hasOwnBorder = isPinned || usesBarRows;
    const optionBg = isSelected
      ? RECAP_OPALITE_BLUE_TINT_08
      : usesBarRows
        ? cardBg
        : 'transparent';
    const optionBorderColor =
      isSelected && hasOwnBorder ? RECAP_OPALITE_BLUE_BORDER : borderColor;
    const optionHoverBg = isClinicalNoteList
      ? RECAP_OPALITE_BLUE_TINT_08
      : isSelected
        ? RECAP_OPALITE_BLUE_TINT_08
        : 'gray.50';
    const optionHoverBorderColor =
      isClinicalNoteList && hasOwnBorder
        ? RECAP_OPALITE_BLUE_BORDER
        : isSelected && hasOwnBorder
          ? RECAP_OPALITE_BLUE_BORDER
          : 'gray.300';

    return (
      <Flex
        key={isPinned ? `selected-template-${t.id}` : t.id}
        ref={templateRef}
        px={{base: 3, sm: 4}}
        py={usesBarRows ? 3 : 4}
        mb={isPinned ? (usesBarRows ? 2 : 3) : undefined}
        align="flex-start"
        position={isPinned && !isClinicalNoteList ? 'sticky' : undefined}
        top={isPinned ? 0 : undefined}
        zIndex={isPinned ? 5 : undefined}
        cursor={isDisabled ? 'default' : 'pointer'}
        bg={optionBg}
        borderWidth={hasOwnBorder ? '1px' : 0}
        borderBottomWidth="1px"
        borderColor={optionBorderColor}
        borderRadius={isPinned ? 'xl' : usesBarRows ? 'lg' : 0}
        _last={hasOwnBorder ? undefined : {borderBottomWidth: 0}}
        _hover={
          isDisabled
            ? {}
            : {
                bg: optionHoverBg,
                borderColor: optionHoverBorderColor,
              }
        }
        _focusVisible={{
          outline: '2px solid',
          outlineColor: accentColor,
          outlineOffset: '-2px',
        }}
        onClick={() => !isDisabled && onSelect(t.id)}
        transition="background 150ms ease, border-color 150ms ease"
        role="radio"
        aria-checked={isSelected}
        tabIndex={0}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
            e.preventDefault();
            onSelect(t.id);
          }
        }}
        minH="56px"
      >
        {usesBarRows ? null : (
          <Flex
            w="22px"
            h="22px"
            mt="2px"
            mr={3}
            flexShrink={0}
            align="center"
            justify="center"
          >
            <Box
              w="22px"
              h="22px"
              borderRadius="full"
              borderWidth="1px"
              borderColor={isSelected ? 'gray' : 'gray.400'}
              bg={isSelected ? accentColor : 'transparent'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              transition="all 150ms ease"
            >
              {isSelected && (
                <Box w="8px" h="8px" borderRadius="full" bg="white" />
              )}
            </Box>
          </Flex>
        )}
        <Box flex={1} minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text
              fontSize="md"
              fontWeight={isClinicalNoteList ? 'medium' : 'normal'}
              color="gray.900"
              lineHeight="short"
            >
              {nameNode || t.name}
            </Text>
            {isSuggested && (
              <Badge
                colorScheme="gray"
                fontSize="sm"
                fontWeight="normal"
                lineHeight="1.25"
                px={2}
                borderRadius="md"
                variant="subtle"
                textTransform="none"
              >
                {isClinicalNoteList ? 'AI suggested' : 'Suggested'}
              </Badge>
            )}
            {t.isEHR && (
              <Badge
                colorScheme="gray"
                fontSize="sm"
                fontWeight="normal"
                lineHeight="1.25"
                px={2}
                borderRadius="md"
                variant="subtle"
                textTransform="none"
              >
                EHR
              </Badge>
            )}
            {t.isCustom && !t.company_id && (
              <Badge
                colorScheme="gray"
                fontSize="sm"
                fontWeight="normal"
                lineHeight="1.25"
                px={2}
                borderRadius="md"
                variant="subtle"
                textTransform="none"
              >
                Custom
              </Badge>
            )}
            {t.company_id && (
              <Badge
                colorScheme="gray"
                fontSize="sm"
                fontWeight="normal"
                lineHeight="1.25"
                px={2}
                borderRadius="md"
                variant="subtle"
                textTransform="none"
              >
                Org
              </Badge>
            )}
          </HStack>
          {t.description && (
            <Text
              fontSize="sm"
              color={isClinicalNoteList ? 'gray.600' : 'gray.700'}
              noOfLines={2}
              mt={isClinicalNoteList ? 2 : 1.5}
              lineHeight="base"
            >
              {t.description}
            </Text>
          )}
        </Box>
        {usesBarRows && isSelected ? (
          <Icon
            as={TablerCheck}
            color={RECAP_OPALITE_BLUE}
            boxSize={5}
            flexShrink={0}
            ml={3}
            aria-label="Currently selected"
          />
        ) : null}
      </Flex>
    );
  };

  return (
    <Box>
      {/* Search – 48px min height for WCAG touch target */}
      <InputGroup
        size="md"
        mb={isClinicalNoteList ? 0 : 3}
        px={isClinicalNoteList ? 0 : {base: 2, sm: 0}}
      >
        {isClinicalNoteList ? null : (
          <InputLeftElement pointerEvents="none" h="48px">
            <TablerSearch color="#545454" size={18} aria-hidden="true" />
          </InputLeftElement>
        )}
        <Input
          placeholder="Search templates"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          borderRadius="lg"
          fontSize="md"
          fontWeight="normal"
          h={isClinicalNoteList ? undefined : '48px'}
          minH={isClinicalNoteList ? '44px' : undefined}
          pl={isClinicalNoteList ? 4 : undefined}
          pr={isClinicalNoteList ? 12 : undefined}
          bg={isClinicalNoteList ? cardBg : undefined}
          borderColor={isClinicalNoteList ? borderColor : 'gray.300'}
          color="gray.900"
          _placeholder={{color: 'gray.600'}}
          isDisabled={isDisabled}
          _focus={{
            boxShadow: 'none',
            borderColor: accentColor,
            outline: '2px solid',
            outlineColor: accentColor,
            outlineOffset: isClinicalNoteList ? '-2px' : '1px',
          }}
          aria-label="Search templates"
        />
        {searchText &&
          (isClinicalNoteList ? (
            <InputRightElement h="100%" w="44px">
              <IconButton
                type="button"
                size="sm"
                boxSize="44px"
                minW="44px"
                variant="ghost"
                borderRadius="lg"
                bg="transparent"
                color="gray.600"
                aria-label="Clear search"
                icon={<TablerX size={16} />}
                onClick={() => setSearchText('')}
                _hover={{bg: 'transparent', color: 'gray.600'}}
                _focus={{bg: 'transparent'}}
                _active={{bg: 'transparent'}}
              />
            </InputRightElement>
          ) : (
            <IconButton
              icon={<TablerX size={16} />}
              size="md"
              variant="opaliteGhost"
              position="absolute"
              right={0.5}
              top="50%"
              transform="translateY(-50%)"
              onClick={() => setSearchText('')}
              aria-label="Clear search"
              zIndex={2}
              minW={RECAP_BUTTON_HEIGHT}
              minH={RECAP_BUTTON_HEIGHT}
              borderRadius="lg"
            />
          ))}
      </InputGroup>
      {isClinicalNoteList ? (
        <Box
          borderTopWidth="1px"
          borderColor="gray.100"
          mx={{base: -5, sm: -6}}
          mt={4}
          mb={4}
        />
      ) : null}

      {/* AI Suggested – fixed-height row across all states */}
      {(isLoadingSuggestion || suggestedTemplate) &&
        (() => {
          const canSelect =
            suggestedTemplate &&
            suggestedId !== selectedId &&
            !isDisabled &&
            !searchText;
          return (
            <Flex
              mb={isClinicalNoteList ? 4 : 3}
              px={isClinicalNoteList ? {base: 3, sm: 3.5} : {base: 3, sm: 4}}
              mx={isClinicalNoteList ? 0 : {base: 2, sm: 0}}
              minH={isClinicalNoteList ? '44px' : RECAP_BUTTON_HEIGHT}
              py={2}
              borderRadius={isClinicalNoteList ? 'xl' : 'lg'}
              bg="gray.50"
              borderWidth="1px"
              borderColor={canSelect ? 'gray.300' : 'gray.200'}
              align="center"
              gap={isClinicalNoteList ? 3 : 2}
              cursor={canSelect ? 'pointer' : 'default'}
              onClick={() => canSelect && onSelect(suggestedId)}
              _hover={
                canSelect
                  ? {
                      borderColor: 'gray.400',
                      bg: 'gray.100',
                    }
                  : {}
              }
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'gray.500',
                outlineOffset: '2px',
              }}
              transition="all 150ms ease"
              role={canSelect ? 'button' : undefined}
              tabIndex={canSelect ? 0 : undefined}
              onKeyDown={e => {
                if ((e.key === 'Enter' || e.key === ' ') && canSelect) {
                  e.preventDefault();
                  onSelect(suggestedId);
                }
              }}
            >
              {isLoadingSuggestion ? (
                <Text fontSize="md" color="gray.800" fontWeight="normal">
                  Finding best template...
                </Text>
              ) : (
                suggestedTemplate && (
                  <>
                    <Icon
                      as={TablerStar}
                      color="gray.600"
                      boxSize={4}
                      flexShrink={0}
                      aria-hidden="true"
                    />
                    <Badge
                      colorScheme="gray"
                      fontSize="sm"
                      fontWeight="normal"
                      lineHeight="1.25"
                      px={2}
                      borderRadius="md"
                      variant="subtle"
                      flexShrink={0}
                      textTransform="none"
                    >
                      {isClinicalNoteList ? 'AI suggested' : 'AI Suggested'}
                    </Badge>
                    <Text
                      fontSize="md"
                      fontWeight="normal"
                      color="gray.800"
                      noOfLines={1}
                      flex={1}
                      minW={0}
                    >
                      {plannerSearchHighlightNode(
                        suggestedTemplate.name,
                        searchText,
                      ) || suggestedTemplate.name}
                    </Text>
                    {suggestedId === selectedId && !isClinicalNoteList && (
                      <Icon
                        as={TablerCheck}
                        color="gray.600"
                        boxSize={5}
                        flexShrink={0}
                        aria-label="Currently selected"
                      />
                    )}
                  </>
                )
              )}
            </Flex>
          );
        })()}

      {/* Template list – grows with the viewport so it doesn't feel cramped
          on tall screens, but stays bounded by the caller-supplied `maxH`. */}
      <Box
        ref={scrollContainerRef}
        overflowY="auto"
        h={listHeight == null ? undefined : `${listHeight}px`}
        transition={
          listHeight == null
            ? undefined
            : `height ${SIDEBAR_HEIGHT_MOTION_MS}ms ${SIDEBAR_HEIGHT_MOTION_EASE}`
        }
        role="radiogroup"
        aria-label="Available templates"
        sx={{
          maxHeight: isClinicalNoteList
            ? `min(${maxH}, 54vh)`
            : `min(${maxH}, 65vh)`,
          '@media (max-width: 480px)': {
            maxHeight: isClinicalNoteList
              ? `min(${maxH}, 48vh)`
              : `min(${maxH}, 55vh)`,
          },
          // Short-display caps. On vertically-constrained monitors (the
          // CIFC workstations etc.) a 65vh list eats the whole pane and
          // pushes the "Create note" action off-screen. Bounding the
          // list more tightly here keeps the search box and the action
          // button on screen while the list scrolls internally. Declared
          // after the width query so a short screen wins ties.
          '@media (max-height: 860px)': {
            maxHeight: isClinicalNoteList
              ? `min(${maxH}, 45vh)`
              : `min(${maxH}, 46vh)`,
          },
          '@media (max-height: 700px)': {
            maxHeight: isClinicalNoteList
              ? `min(${maxH}, 38vh)`
              : `min(${maxH}, 38vh)`,
          },
          // The card grows/shrinks smoothly as drawers open and close — but
          // never animate motion for users who've asked the OS to reduce it.
          '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
          ...scrollbarSx,
        }}
      >
        {/* Unconstrained wrapper used to measure the true content height so the
            scroll container can animate to hug its contents (see
            rememberListHeight). Without this ref the height transition is a
            no-op and drawers pop open instantly. */}
        <Box ref={contentRef}>
          {selectedTemplate && !isClinicalNoteList
            ? renderTemplateOption(selectedTemplate, {
                isPinned: true,
                templateRef: selectedStickyRef,
              })
            : null}
          {filtered.length === 0 ? (
            <Flex direction="column" align="center" py={8} px={4}>
              <Icon as={TablerSearch} color="gray.600" boxSize={7} mb={2} />
              <Text
                fontSize="md"
                color="gray.700"
                fontWeight="normal"
                textAlign="center"
              >
                No templates match &ldquo;{searchText}&rdquo;
              </Text>
            </Flex>
          ) : (
            <VStack spacing={usesBarRows ? 3 : 4} align="stretch">
              {grouped.map((group, gi) => {
                const isCollapsed = group.category
                  ? collapsedCategories.has(group.category)
                  : false;
                const visibleTemplates = group.templates.filter((t: any) =>
                  isClinicalNoteList ? true : t.id !== selectedId,
                );
                const contentId = group.category
                  ? `category-panel-${group.category}`
                  : undefined;
                const isOpen = !isCollapsed;

                if (isClinicalNoteList && group.category) {
                  return (
                    <Box
                      key={group.category}
                      data-testid="clinical-note-template-category-group"
                    >
                      <Box
                        as="button"
                        type="button"
                        ref={(el: any) => {
                          categoryRefs.current[String(group.category)] = el;
                        }}
                        px={{base: 3, sm: 4}}
                        py={3}
                        minH={`${CATEGORY_STICKY_HEIGHT}px`}
                        position="sticky"
                        top={categoryStickyTop}
                        zIndex={3}
                        bg={isOpen ? 'gray.50' : categoryHeaderBg}
                        borderWidth={0}
                        borderBottomWidth={isOpen ? 0 : '1px'}
                        borderColor="gray.100"
                        borderTopRadius={isOpen ? 'xl' : 0}
                        borderBottomRadius={0}
                        textAlign="left"
                        w="100%"
                        cursor="pointer"
                        transition="background 150ms ease"
                        onClick={() => toggleCategory(group.category)}
                        _hover={{bg: 'gray.50'}}
                        _focusVisible={{
                          outline: '2px solid',
                          outlineColor: accentColor,
                          outlineOffset: '-2px',
                        }}
                        aria-expanded={!isCollapsed}
                        aria-controls={contentId}
                        onKeyDown={(e: any) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleCategory(group.category);
                          }
                        }}
                      >
                        <Flex align="center" justify="space-between" gap={3}>
                          <Text
                            as="h3"
                            color="gray.900"
                            fontSize="md"
                            fontWeight="semibold"
                            letterSpacing="-0.01em"
                            lineHeight="1.25"
                          >
                            {group.category}
                          </Text>
                          <Flex align="center" gap={2.5} flexShrink={0}>
                            <Box
                              data-testid="clinical-note-template-category-count"
                              bg="gray.100"
                              color="gray.700"
                              borderWidth="1px"
                              borderColor="gray.200"
                              borderRadius="md"
                              px={2}
                              py={0.5}
                              minW="1.75rem"
                              textAlign="center"
                              fontSize="sm"
                              fontWeight="medium"
                              lineHeight="1.4"
                            >
                              {group.templates.length}
                            </Box>
                            <Box
                              as="span"
                              aria-hidden="true"
                              color={isOpen ? accentColor : 'gray.500'}
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                              transform={
                                isOpen ? 'rotate(180deg)' : 'rotate(0)'
                              }
                              sx={CHEVRON_MOTION_SX}
                            >
                              <TablerChevronDown size={18} focusable="false" />
                            </Box>
                          </Flex>
                        </Flex>
                      </Box>
                      {!isCollapsed && visibleTemplates.length > 0 && (
                        <Box
                          id={contentId}
                          overflow="visible"
                          bg="gray.50"
                          borderBottomRadius="xl"
                          px={2}
                          pt={1}
                          pb={2}
                          sx={DRAWER_CONTENT_SX}
                        >
                          <VStack spacing={2} align="stretch">
                            {visibleTemplates.map((t: any) =>
                              renderTemplateOption(t),
                            )}
                          </VStack>
                        </Box>
                      )}
                    </Box>
                  );
                }

                return (
                  <Box
                    key={group.category || `_uncategorized_${gi}`}
                    data-testid={
                      isClinicalNoteList
                        ? 'clinical-note-template-category-group'
                        : undefined
                    }
                  >
                    {group.category &&
                      (() => {
                        // In the card variant the header and its expanded
                        // rows read as one connected card, so the header drops
                        // its bottom radius/border when open and the content
                        // tucks flush beneath it.
                        const isConnectedCard = !usesBarRows;
                        const headerOpen = isConnectedCard && isOpen;
                        const templateCount = group.templates.length;
                        const ChevronIcon = isCollapsed
                          ? TablerChevronDown
                          : TablerChevronUp;
                        return (
                          <Box
                            as="button"
                            type="button"
                            ref={(el: any) => {
                              categoryRefs.current[String(group.category)] = el;
                            }}
                            px={isConnectedCard ? {base: 3, sm: 4} : 0}
                            py={isConnectedCard ? 3 : 1.5}
                            mb={isConnectedCard ? 0 : 0.5}
                            minH={`${CATEGORY_STICKY_HEIGHT}px`}
                            position="sticky"
                            top={categoryStickyTop}
                            zIndex={3}
                            bg={
                              isConnectedCard
                                ? isOpen
                                  ? 'gray.50'
                                  : 'white'
                                : categoryHeaderBg
                            }
                            borderWidth={isConnectedCard ? '1px' : 0}
                            borderBottomWidth={headerOpen ? 0 : undefined}
                            borderColor={
                              isConnectedCard && isOpen
                                ? 'gray.300'
                                : 'gray.200'
                            }
                            borderRadius={isConnectedCard ? 'xl' : 0}
                            borderBottomRadius={headerOpen ? 0 : undefined}
                            textAlign="left"
                            w="100%"
                            cursor="pointer"
                            transition="background 150ms ease, border-color 150ms ease"
                            onClick={() => toggleCategory(group.category)}
                            _hover={
                              isConnectedCard
                                ? {bg: 'gray.50', borderColor: 'gray.300'}
                                : {bg: categoryHeaderBg}
                            }
                            _focusVisible={
                              isConnectedCard
                                ? {
                                    outline: '2px solid',
                                    outlineColor: accentColor,
                                    outlineOffset: '-2px',
                                  }
                                : plannerStyles.focusOutlineStyles
                            }
                            aria-expanded={!isCollapsed}
                            aria-controls={contentId}
                            onKeyDown={(e: any) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleCategory(group.category);
                              }
                            }}
                          >
                            <Flex
                              align="center"
                              justify="space-between"
                              gap={3}
                            >
                              <Flex align="center" gap={3} minW={0}>
                                {isConnectedCard ? (
                                  <Flex
                                    w="34px"
                                    h="34px"
                                    borderRadius="lg"
                                    bg={isOpen ? 'white' : 'gray.100'}
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    color="gray.600"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                    transition="background 150ms ease"
                                  >
                                    <TablerStack2
                                      size={16}
                                      aria-hidden="true"
                                    />
                                  </Flex>
                                ) : null}
                                <Box minW={0}>
                                  <Text
                                    as="h3"
                                    color={
                                      isConnectedCard
                                        ? 'gray.800'
                                        : plannerStyles.sectionLabelColor
                                    }
                                    fontSize="md"
                                    fontWeight="semibold"
                                    lineHeight="1.25"
                                    noOfLines={1}
                                  >
                                    {group.category}
                                  </Text>
                                  {isConnectedCard ? (
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      fontWeight="normal"
                                      lineHeight="1.3"
                                      mt={0.5}
                                    >
                                      {templateCount}{' '}
                                      {templateCount === 1
                                        ? 'template'
                                        : 'templates'}
                                    </Text>
                                  ) : null}
                                </Box>
                              </Flex>
                              <Flex align="center" gap={2} flexShrink={0}>
                                {isConnectedCard ? null : (
                                  <Box
                                    data-testid={
                                      isClinicalNoteList
                                        ? 'clinical-note-template-category-count'
                                        : undefined
                                    }
                                    bg={plannerStyles.countBg}
                                    color={plannerStyles.countColor}
                                    borderRadius="full"
                                    px={2.5}
                                    py={0.5}
                                    minW="2.25rem"
                                    textAlign="center"
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight="1.4"
                                  >
                                    {templateCount}
                                  </Box>
                                )}
                                <Box
                                  as="span"
                                  aria-hidden="true"
                                  color={
                                    isConnectedCard
                                      ? isOpen
                                        ? accentColor
                                        : 'gray.500'
                                      : plannerStyles.sectionLabelColor
                                  }
                                  display="inline-flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flexShrink={0}
                                  transform={
                                    isConnectedCard && isOpen
                                      ? 'rotate(180deg)'
                                      : 'rotate(0)'
                                  }
                                  sx={
                                    isConnectedCard
                                      ? CHEVRON_MOTION_SX
                                      : undefined
                                  }
                                >
                                  {isConnectedCard ? (
                                    <TablerChevronDown
                                      size={18}
                                      focusable="false"
                                    />
                                  ) : (
                                    <ChevronIcon size={16} focusable="false" />
                                  )}
                                </Box>
                              </Flex>
                            </Flex>
                          </Box>
                        );
                      })()}
                    {!isCollapsed && visibleTemplates.length > 0 && (
                      <Box
                        id={contentId}
                        bg={usesBarRows ? 'transparent' : cardBg}
                        borderWidth={usesBarRows ? 0 : '1px'}
                        borderTopWidth={
                          group.category && !usesBarRows ? 0 : undefined
                        }
                        borderColor={
                          group.category && !usesBarRows
                            ? 'gray.300'
                            : borderColor
                        }
                        borderRadius={usesBarRows ? 0 : 'xl'}
                        borderTopRadius={
                          group.category && !usesBarRows ? 0 : undefined
                        }
                        overflow="hidden"
                        mt={0}
                        sx={DRAWER_CONTENT_SX}
                      >
                        <VStack spacing={usesBarRows ? 2 : 0} align="stretch">
                          {visibleTemplates.map((t: any) =>
                            renderTemplateOption(t),
                          )}
                        </VStack>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SearchableTemplatePicker;
